import React, { useContext, useEffect, useRef, useState } from "react";
import { Collapse, Toast } from "react-bootstrap";

export type InfoPanelItem = {
  title: string;
  description: string;
};

export type InfoPanelContextType = {
  item?: InfoPanelItem;
  setItem: (item: InfoPanelItem | undefined) => void;
};

const InfoPanelContext = React.createContext<InfoPanelContextType>({
  setItem: () => { },
});

export const InfoPanelProvider: React.FC = React.memo(({ children }) => {
  const [value, setValue] = useState<InfoPanelContextType["item"]>();
  const ctxValue: InfoPanelContextType = React.useMemo(() => ({ item: value, setItem: setValue, }), [value]);
  return (
    <InfoPanelContext.Provider value={ctxValue}>
      <InfoPanel />
      {children}
    </InfoPanelContext.Provider>
  );
});

export const useInfoPanel = (): InfoPanelContextType => {
  return useContext(InfoPanelContext);
};

export function InfoPanel() {
  const panelContext = useContext(InfoPanelContext);
  const [show, setShow] = useState(false);
  const currentRef = useRef<InfoPanelItem>();
  const btnCloseModal: React.RefObject<HTMLButtonElement> = React.createRef();

  useEffect(() => {
    if (!show) {
      currentRef.current = panelContext.item;
      setShow(!!currentRef.current);
    } else {
      setShow(false);
    }
  }, [panelContext.item]);

  const onExited = () => {
    currentRef.current = panelContext.item;
    setShow(!!currentRef.current);
  };

  const setFocus = () => {
    if (btnCloseModal.current) {
      btnCloseModal.current.focus();
    }
  };

  const onClose = () => {
    panelContext.setItem(undefined);
  };

  const item = currentRef.current;

  return (
    <React.Fragment>
      {show && <div className="d-md-none modal-backdrop fade show" onClick={onClose}></div>}
      <div className="fixed-bottom d-md-flex justify-content-md-center z-index-modal">
        <Collapse in={show} onExited={onExited} onEntered={setFocus}>
          <div className="mw-md-medium bg-primary text-white w-100 radius-0 p-3 position-relative mb-md-4 shadow">
            <div className="position-absolute d-flex w-100 top-0">
              <button ref={btnCloseModal} className="ml-auto mr-5 mt-1 px-1 btn-no-styling"
                type="button"
                aria-label="close popup"
                onClick={onClose}>
                <img src="/img/svg/icon-x-white.svg" alt="" />
              </button>
            </div>

            <div className="mb-3 d-flex align-items-center">
              <img src="/img/svg/icon-info.svg" className="mr-2" alt=""/>
              {item?.title}
            </div>
            <div className="text-small last-child-no-margin" dangerouslySetInnerHTML={{ __html: item?.description || '' }} />
          </div>
        </Collapse>
      </div>
    </React.Fragment>
  );
}
